import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import Layout from '../components/layout/Layout';
import { API_BASE } from '../api';

// 🎨 UPDATED GLOBAL COLORS DEFINITION - Using #302b63 as primary background
const colors = {
    // Background/Elements - All using #302b63 variations
    backgroundDark: '#0f0c29', 
    backgroundMedium: '#302b63', // Primary background color
    backgroundLight: '#24243e',
    backgroundInput: '#302b63', // Input background
    textPrimary: '#FFFFFF',
    textSecondary: 'rgba(255, 255, 255, 0.8)',
    textTertiary: 'rgba(255, 255, 255, 0.6)',
    borderDark: 'rgba(255, 255, 255, 0.2)',
    borderSecondary: 'rgba(255, 255, 255, 0.15)',

    // Primary Colors and Gradients
    primary: '#6366F1', // Indigo/Violet
    secondary: '#8B5CF6', // Purple/Pink
    accent: '#06B6D4', // Cyan
    success: '#10B981', // Green
    warning: '#F59E0B', // Orange
    danger: '#EF4444', // Red

    // Gradients using #302b63
    gradientMain: 'linear-gradient(135deg, #302b63 0%, #24243e 100%)',
    gradient: 'linear-gradient(135deg, #302b63 0%, #24243e 100%)', 
    gradient2: 'linear-gradient(135deg, #302b63 0%, #1a1a3a 100%)', 
    gradient3: 'linear-gradient(135deg, #302b63 0%, #2a2a5a 100%)', 
    gradient4: 'linear-gradient(135deg, #302b63 0%, #3a3a7a 100%)' 
};

function MyWork() {
    const [tasks, setTasks] = useState([]);
    const [filteredTasks, setFilteredTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTask, setSelectedTask] = useState(null);
    const [showSubmissionForm, setShowSubmissionForm] = useState(false);
    const [showTaskDetailsModal, setShowTaskDetailsModal] = useState(false);
    const [submissionData, setSubmissionData] = useState({
        thumbnail_url: '',
        url: '',
        notes: '',
        status: 'todo'
    });
    const [submitting, setSubmitting] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [selectedStatusForUpdate, setSelectedStatusForUpdate] = useState('');
    const [taskToUpdateStatus, setTaskToUpdateStatus] = useState(null);
    
    const navigate = useNavigate();
    const { currentTheme } = useContext(ThemeContext);

    const userRole = sessionStorage.getItem('userRole');
    const userId = sessionStorage.getItem('userId');
    const userName = sessionStorage.getItem('userName') || 'Designer';

    // Get status filter from URL or default to 'all'
    const statusFilter = searchParams.get('status') || 'all';

    useEffect(() => {
        if (userRole !== 'designer') {
            navigate('/dashboard');
            return;
        }
        fetchMyWork();
    }, []);

    useEffect(() => {
        filterTasks();
    }, [tasks, statusFilter, searchTerm]);

    const fetchMyWork = async () => {
        try {
            if (!refreshing) setLoading(true);
            setError('');

            const response = await fetch(`${API_BASE}/designer_api.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    operation: 'get_designer_tasks',
                    designer_id: parseInt(userId)
                })
            });

            const text = await response.text();
            let data;
            
            try {
                data = JSON.parse(text);
            } catch (parseError) {
                throw new Error('Invalid JSON response from server');
            }
            
            if (!response.ok || !data.success) {
                throw new Error(data.error || `Error loading tasks: HTTP status ${response.status}`);
            }
            
            setTasks(data.tasks || []);

        } catch (error) {
            console.error('Error fetching my work:', error);
            setError('Error loading tasks: ' + error.message);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const filterTasks = () => {
        let filtered = tasks;

        // Apply status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(task => task.status === statusFilter);
        }

        // Apply search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(task => 
                (task.video_name && task.video_name.toLowerCase().includes(term)) ||
                (task.description && task.description.toLowerCase().includes(term)) ||
                (task.raw_link && task.raw_link.toLowerCase().includes(term)) ||
                (task.assigned_by_name && task.assigned_by_name.toLowerCase().includes(term)) ||
                (task.created_at && task.created_at.toLowerCase().includes(term))
            );
        }

        setFilteredTasks(filtered);
    };

    const handleRefresh = () => {
        setRefreshing(true);
        fetchMyWork();
    };

    const handleStatusFilter = (status) => {
        if (status === 'all') {
            searchParams.delete('status');
        } else {
            searchParams.set('status', status);
        }
        setSearchParams(searchParams);
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleStartWork = (task) => {
        // Don't show any modal for approved tasks
        if (task.status === 'approved') {
            return;
        }
        
        if (task.status === 'completed' || task.status === 'needs_revision') {
            // Show task details for completed or needs_revision tasks
            setSelectedTask(task);
            setShowTaskDetailsModal(true);
        } else {
            // Show submission form for todo and inprogress tasks
            setSelectedTask(task);
            setSubmissionData({
                thumbnail_url: task.thumbnail_url || '',
                url: task.submitted_url || '',
                notes: task.designer_notes || '',
                status: task.status || 'todo'
            });
            setShowSubmissionForm(true);
        }
    };

    const handleSubmitTask = async (e) => {
        e.preventDefault();
        if (!selectedTask) return;

        try {
            setSubmitting(true);
            setError('');

            const response = await fetch(`${API_BASE}/designer_api.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    operation: 'submit_designer_task',
                    task_id: selectedTask.id,
                    designer_id: parseInt(userId),
                    thumbnail_url: submissionData.thumbnail_url.trim() || '', // Send empty string if not provided
                    url: submissionData.url.trim() || '', // Send empty string if not provided
                    notes: submissionData.notes.trim() || '',
                    status: submissionData.status
                })
            });

            const text = await response.text();
            let data;
            
            try {
                data = JSON.parse(text);
            } catch (parseError) {
                throw new Error('Invalid JSON response from server');
            }
            
            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Error submitting task');
            }

            // Refresh tasks list
            fetchMyWork();
            setShowSubmissionForm(false);
            setSelectedTask(null);
            setSubmissionData({
                thumbnail_url: '',
                url: '',
                notes: '',
                status: 'todo'
            });

        } catch (error) {
            console.error('Error submitting task:', error);
            setError('Error submitting task: ' + error.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSubmissionData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle status update from dropdown
    const handleStatusUpdate = async (taskId, newStatus) => {
        try {
            setUpdatingStatus(true);
            setError('');

            const response = await fetch(`${API_BASE}/designer_api.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    operation: 'update_task_status',
                    designer_id: parseInt(userId),
                    task_id: taskId,
                    status: newStatus
                })
            });

            const text = await response.text();
            let data;
            
            try {
                data = JSON.parse(text);
            } catch (parseError) {
                throw new Error('Invalid JSON response from server');
            }
            
            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Error updating task status');
            }

            // Refresh tasks list
            fetchMyWork();
            setTaskToUpdateStatus(null);
            setSelectedStatusForUpdate('');

        } catch (error) {
            console.error('Error updating task status:', error);
            setError('Error updating task status: ' + error.message);
        } finally {
            setUpdatingStatus(false);
        }
    };

    // Open status update dropdown for a specific task
    const openStatusUpdate = (task, e) => {
        e.stopPropagation(); // Prevent triggering other click events
        setTaskToUpdateStatus(task);
        setSelectedStatusForUpdate(task.status);
    };

    // Get status badge with CheckInOut.js styling
    const getStatusBadge = (status, taskId = null) => {
        const statusConfig = {
            todo: { class: 'bg-secondary', text: 'To Do', icon: 'fas fa-clock' },
            inprogress: { class: 'bg-info', text: 'In Progress', icon: 'fas fa-palette' },
            completed: { class: 'bg-success', text: 'Completed', icon: 'fas fa-check-circle' },
            approved: { class: 'bg-success', text: 'Approved', icon: 'fas fa-thumbs-up' },
            needs_revision: { class: 'bg-danger', text: 'Needs Revision', icon: 'fas fa-redo' }
        };
        
        const config = statusConfig[status] || statusConfig.todo;
        
        // If this is the task being updated, show dropdown
        if (taskId && taskToUpdateStatus && taskToUpdateStatus.id === taskId) {
            return (
                <div style={{ position: 'relative', minWidth: '150px' }}>
                    <select
                        value={selectedStatusForUpdate}
                        onChange={(e) => {
                            const newStatus = e.target.value;
                            setSelectedStatusForUpdate(newStatus);
                            handleStatusUpdate(taskId, newStatus);
                        }}
                        disabled={updatingStatus}
                        style={{
                            width: '100%',
                            padding: '6px 12px',
                            borderRadius: '15px',
                            border: 'none',
                            background: config.class === 'bg-danger' ? '#dc3545' :
                                      config.class === 'bg-success' ? '#198754' :
                                      config.class === 'bg-info' ? '#0dcaf0' :
                                      config.class === 'bg-secondary' ? '#6c757d' : '#0d6efd',
                            color: 'white',
                            fontWeight: '600',
                            fontSize: '0.8rem',
                            cursor: 'pointer',
                            appearance: 'none',
                            textAlign: 'center'
                        }}
                    >
                        <option value="todo" style={{ background: '#6c757d' }}>To Do</option>
                        <option value="inprogress" style={{ background: '#0dcaf0' }}>In Progress</option>
                        <option value="completed" style={{ background: '#198754' }}>Completed</option>
                        <option value="needs_revision" style={{ background: '#dc3545' }}>Needs Revision</option>
                    </select>
                    {updatingStatus && taskToUpdateStatus.id === taskId && (
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            right: '10px',
                            transform: 'translateY(-50%)'
                        }}>
                            <i className="fas fa-spinner fa-spin text-white"></i>
                        </div>
                    )}
                </div>
            );
        }
        
        return (
            <span 
                className={`badge ${config.class} text-white`}
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={(e) => openStatusUpdate({ id: taskId, status }, e)}
            >
                <i className={`${config.icon} me-1`}></i>
                {config.text}
            </span>
        );
    };

    const getActionButtonText = (status) => {
        switch(status) {
            case 'todo':
                return 'Start Work';
            case 'inprogress':
                return 'Submit Work';
            case 'completed':
                return 'View Details';
            case 'needs_revision':
                return 'Fix & Resubmit';
            case 'approved':
                return 'No Action Required';
            default:
                return 'Start Work';
        }
    };

    const getActionButtonIcon = (status) => {
        switch(status) {
            case 'todo':
                return 'fas fa-play';
            case 'inprogress':
                return 'fas fa-paper-plane';
            case 'completed':
                return 'fas fa-eye';
            case 'needs_revision':
                return 'fas fa-redo';
            case 'approved':
                return 'fas fa-check-circle';
            default:
                return 'fas fa-play';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === 'NULL' || dateString === 'null') return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString || dateString === 'NULL' || dateString === 'null') return 'Not available';
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getTaskCounts = () => {
        return {
            all: tasks.length,
            todo: tasks.filter(task => task.status === 'todo').length,
            inprogress: tasks.filter(task => task.status === 'inprogress').length,
            completed: tasks.filter(task => task.status === 'completed').length,
            needs_revision: tasks.filter(task => task.status === 'needs_revision').length
        };
    };

    const taskCounts = getTaskCounts();

    // Get button text based on selected status
    const getSubmitButtonText = () => {
        switch(submissionData.status) {
            case 'todo':
                return 'Mark as To Do';
            case 'inprogress':
                return 'Mark as In Progress';
            case 'completed':
                return 'Mark as Completed';
            default:
                return 'Update Task';
        }
    };

    // Get button icon based on selected status
    const getSubmitButtonIcon = () => {
        switch(submissionData.status) {
            case 'todo':
                return 'fas fa-clock';
            case 'inprogress':
                return 'fas fa-palette';
            case 'completed':
                return 'fas fa-check-circle';
            default:
                return 'fas fa-save';
        }
    };

    // Check if link is valid
    const isValidLink = (link) => {
        if (!link || typeof link !== 'string') return false;
        const invalidValues = ['-', 'Open', 'NULL', 'null', ''];
        return !invalidValues.includes(link.trim());
    };

    // Handle link click
    const handleLinkClick = (link) => {
        if (isValidLink(link)) {
            const formattedLink = link.startsWith('http') ? link : `https://${link}`;
            window.open(formattedLink, '_blank');
        }
    };

    // Get current time and date for header
    const getCurrentTime = () => {
        return new Date().toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const getCurrentDate = () => {
        return new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <Layout>
                <div style={{minHeight: '100vh', background: `linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)`, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                    <div style={{textAlign: 'center', color: 'white'}}>
                        <div className="loading-dots" style={styles.loadingDots}>
                            <div className="dot" style={styles.dot}></div>
                            <div className="dot" style={styles.dot}></div>
                            <div className="dot" style={styles.dot}></div>
                        </div>
                        <h3 style={styles.loadingText}>Loading Your Work</h3>
                        <p style={styles.loadingSubtext}>Getting your tasks ready</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div style={{minHeight: '100vh'}}>
                <div className="mywork-container" style={styles.container}>
                    {/* Animated Background */}
                    <div className="animated-background" style={styles.animatedBackground}>
                        <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
                        <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
                        <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
                        <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
                        <div className="grid-overlay" style={styles.gridOverlay}></div>
                    </div>

                    {/* Header Section */}
                    <div className="mywork-header" style={styles.header}>
                        <div className="header-content" style={styles.headerContent}>
                            <div className="welcome-section">
                                <div className="greeting" style={styles.greeting}>
                                    <h1 className="page-title" style={styles.pageTitle}>
                                        <i className="fas fa-tasks me-2"></i>
                                        My Work
                                    </h1>
                                    <div className="welcome-badge" style={styles.welcomeBadge}>
                                        <span className="badge-text" style={styles.badgeText}>Hello, {userName}!</span>
                                        <span className="badge-role" style={styles.badgeRole}>Designer</span>
                                    </div>
                                </div>
                                <p className="page-subtitle" style={styles.pageSubtitle}>
                                    Manage your design tasks and projects
                                </p>
                            </div>
                            <div className="header-actions" style={styles.headerActions}>
                                <div className="time-display" style={styles.timeDisplay}>
                                    <div className="current-time" style={styles.currentTime}>
                                        {getCurrentTime()}
                                    </div>
                                    <div className="current-date" style={styles.currentDate}>
                                        {getCurrentDate()}
                                    </div>
                                </div>
                                <button 
                                    className="refresh-btn"
                                    onClick={handleRefresh}
                                    disabled={refreshing}
                                    style={styles.refreshBtn}
                                >
                                    <div className="refresh-icon" style={styles.refreshIcon}>
                                        <i className={`fas fa-sync ${refreshing ? 'spinning' : ''}`}></i>
                                    </div>
                                    <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="message-alert" style={{
                            ...styles.messageAlert,
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: `1px solid ${colors.danger}`,
                            color: colors.danger
                        }}>
                            <div className="alert-icon" style={{...styles.alertIcon, color: colors.danger}}>
                                <i className="fas fa-exclamation-triangle"></i>
                            </div>
                            <div className="alert-message" style={styles.alertMessage}>
                                <strong>Error</strong>
                                <span style={{color: colors.textPrimary}}>{error}</span>
                            </div>
                            <button className="alert-close" onClick={() => setError('')} style={styles.alertClose}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                    )}

                    {/* Main Content */}
                    <div className="mywork-content" style={styles.content}>
                        {/* Statistics Cards */}
                        <div className="stats-section" style={styles.statsSection}>
                            <div className="section-title" style={styles.sectionTitle}>
                                <h2 style={{color: colors.textPrimary}}>Task Overview</h2>
                                <p style={{color: colors.textSecondary}}>Your work statistics - Click status badges to update</p>
                            </div>
                            <div className="stats-grid" style={styles.statsGrid}>
                                <div className="stat-card" style={{...styles.statCard, background: colors.gradient}}>
                                    <div className="stat-glow" style={styles.statGlow}></div>
                                    <div className="stat-content" style={styles.statContent}>
                                        <div className="stat-icon" style={styles.statIcon}>
                                            <i className="fas fa-tasks"></i>
                                        </div>
                                        <div className="stat-info" style={styles.statInfo}>
                                            <h3 className="stat-number" style={styles.statNumber}>{taskCounts.all}</h3>
                                            <p className="stat-label" style={styles.statLabel}>All Tasks</p>
                                            <span className="stat-trend" style={styles.statTrend}>Total assigned</span>
                                        </div>
                                    </div>
                                    <div className="stat-wave" style={styles.statWave}></div>
                                </div>

                                <div className="stat-card" style={{...styles.statCard, background: colors.gradient2}}>
                                    <div className="stat-glow" style={styles.statGlow}></div>
                                    <div className="stat-content" style={styles.statContent}>
                                        <div className="stat-icon" style={styles.statIcon}>
                                            <i className="fas fa-clock"></i>
                                        </div>
                                        <div className="stat-info" style={styles.statInfo}>
                                            <h3 className="stat-number" style={styles.statNumber}>{taskCounts.todo}</h3>
                                            <p className="stat-label" style={styles.statLabel}>To Do</p>
                                            <span className="stat-trend" style={styles.statTrend}>Click to update status</span>
                                        </div>
                                    </div>
                                    <div className="stat-wave" style={styles.statWave}></div>
                                </div>

                                <div className="stat-card" style={{...styles.statCard, background: colors.gradient3}}>
                                    <div className="stat-glow" style={styles.statGlow}></div>
                                    <div className="stat-content" style={styles.statContent}>
                                        <div className="stat-icon" style={styles.statIcon}>
                                            <i className="fas fa-palette"></i>
                                        </div>
                                        <div className="stat-info" style={styles.statInfo}>
                                            <h3 className="stat-number" style={styles.statNumber}>{taskCounts.inprogress}</h3>
                                            <p className="stat-label" style={styles.statLabel}>In Progress</p>
                                            <span className="stat-trend" style={styles.statTrend}>Click to update status</span>
                                        </div>
                                    </div>
                                    <div className="stat-wave" style={styles.statWave}></div>
                                </div>

                                <div className="stat-card" style={{...styles.statCard, background: colors.gradient4}}>
                                    <div className="stat-glow" style={styles.statGlow}></div>
                                    <div className="stat-content" style={styles.statContent}>
                                        <div className="stat-icon" style={styles.statIcon}>
                                            <i className="fas fa-check-circle"></i>
                                        </div>
                                        <div className="stat-info" style={styles.statInfo}>
                                            <h3 className="stat-number" style={styles.statNumber}>{taskCounts.completed}</h3>
                                            <p className="stat-label" style={styles.statLabel}>Completed</p>
                                            <span className="stat-trend" style={styles.statTrend}>Click to update status</span>
                                        </div>
                                    </div>
                                    <div className="stat-wave" style={styles.statWave}></div>
                                </div>

                                {/* Needs Revision Stat Card */}
                                <div className="stat-card" style={{...styles.statCard, background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)'}}>
                                    <div className="stat-glow" style={styles.statGlow}></div>
                                    <div className="stat-content" style={styles.statContent}>
                                        <div className="stat-icon" style={styles.statIcon}>
                                            <i className="fas fa-redo"></i>
                                        </div>
                                        <div className="stat-info" style={styles.statInfo}>
                                            <h3 className="stat-number" style={styles.statNumber}>{taskCounts.needs_revision}</h3>
                                            <p className="stat-label" style={styles.statLabel}>Needs Revision</p>
                                            <span className="stat-trend" style={styles.statTrend}>Click to update status</span>
                                        </div>
                                    </div>
                                    <div className="stat-wave" style={styles.statWave}></div>
                                </div>
                            </div>
                        </div>

                        {/* Filters and Search */}
                        <div className="filters-section" style={styles.filtersSection}>
                            <div className="search-card" style={styles.searchCard}>
                                <div className="card-body" style={styles.cardBody}>
                                    <div className="row align-items-center">
                                        <div className="col-md-6 mb-3 mb-md-0">
                                            <div className="input-group">
                                                <span className="input-group-text" style={styles.inputGroupText}>
                                                    <i className="fas fa-search" style={{color: colors.textSecondary}}></i>
                                                </span>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    style={styles.searchInput}
                                                    placeholder="Search tasks by video name, description, or date..."
                                                    value={searchTerm}
                                                    onChange={handleSearch}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="d-flex flex-wrap gap-2 justify-content-md-end">
                                                {['all', 'todo', 'inprogress', 'completed', 'needs_revision'].map((status) => (
                                                    <button 
                                                        key={status}
                                                        className={`filter-btn ${statusFilter === status ? 'active' : ''}`}
                                                        onClick={() => handleStatusFilter(status)}
                                                        style={{
                                                            ...styles.filterBtn,
                                                            ...(statusFilter === status ? styles.filterBtnActive : {})
                                                        }}
                                                    >
                                                        {status === 'all' && 'All'}
                                                        {status === 'todo' && 'To Do'}
                                                        {status === 'inprogress' && 'In Progress'}
                                                        {status === 'completed' && 'Completed'}
                                                        {status === 'needs_revision' && 'Needs Revision'}
                                                        ({status === 'all' ? taskCounts.all :
                                                          status === 'todo' ? taskCounts.todo :
                                                          status === 'inprogress' ? taskCounts.inprogress :
                                                          status === 'completed' ? taskCounts.completed :
                                                          taskCounts.needs_revision})
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Tasks Table */}
                        <div className="tasks-section" style={styles.tasksSection}>
                            <div className="section-header" style={styles.sectionHeader}>
                                <h3 style={{color: colors.textPrimary}}>
                                    {statusFilter === 'all' && 'All Tasks'}
                                    {statusFilter === 'todo' && 'To Do Tasks'}
                                    {statusFilter === 'inprogress' && 'Tasks In Progress'}
                                    {statusFilter === 'completed' && 'Completed Tasks'}
                                    {statusFilter === 'needs_revision' && 'Tasks Needing Revision'}
                                </h3>
                                <span className="tasks-count" style={styles.tasksCount}>
                                    {filteredTasks.length} tasks
                                </span>
                            </div>
                            
                            <div className="tasks-card" style={styles.tasksCard}>
                                {filteredTasks.length === 0 ? (
                                    <div className="empty-state" style={styles.emptyState}>
                                        <div className="empty-icon" style={styles.emptyIcon}>
                                            <i className="fas fa-clipboard-list"></i>
                                        </div>
                                        <h3 style={styles.emptyTitle}>No tasks found</h3>
                                        <p style={styles.emptyText}>
                                            {searchTerm 
                                                ? 'No tasks match your search criteria'
                                                : `You don't have any ${statusFilter !== 'all' ? statusFilter.replace('_', ' ') : ''} tasks at the moment`
                                            }
                                        </p>
                                        {searchTerm && (
                                            <button 
                                                className="clear-search-btn"
                                                onClick={() => setSearchTerm('')}
                                                style={styles.clearSearchBtn}
                                            >
                                                Clear Search
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="table-container" style={styles.tableContainer}>
                                        <table className="tasks-table" style={styles.tasksTable}>
                                            <thead>
                                                <tr>
                                                    {['Task Name', 'Raw Link', 'Description', 'Status', 'Assigned Date', 'Actions'].map((header, index) => (
                                                        <th key={index} style={styles.tableHeader}>
                                                            {header}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredTasks.map((task) => (
                                                    <tr key={task.id} style={styles.tableRow}>
                                                        <td style={styles.tableCell}>
                                                            <strong style={{color: colors.textPrimary}}>{task.video_name || 'Untitled Task'}</strong>
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            {isValidLink(task.raw_link) ? (
                                                                <button 
                                                                    className="link-btn"
                                                                    onClick={() => handleLinkClick(task.raw_link)}
                                                                    style={styles.linkBtn}
                                                                >
                                                                    <i className="fas fa-link me-1"></i>
                                                                    View Raw Link
                                                                </button>
                                                            ) : (
                                                                <span style={styles.noLink}>No Link</span>
                                                            )}
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            <span style={{color: colors.textSecondary}}>
                                                                {task.description || 'No description provided'}
                                                            </span>
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            {getStatusBadge(task.status, task.id)}
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            <span style={{color: colors.textSecondary}}>
                                                                {formatDate(task.created_at)}
                                                            </span>
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            {/* Hide action button for approved and completed tasks */}
                                                            {task.status === 'approved' || task.status === 'completed' ? (
                                                                <span style={{color: colors.textTertiary, fontSize: '0.85rem'}}>
                                                                    No action required
                                                                </span>
                                                            ) : (
                                                                <button 
                                                                    className="action-btn"
                                                                    onClick={() => handleStartWork(task)}
                                                                    style={{
                                                                        ...styles.actionBtn,
                                                                        ...(task.status === 'needs_revision' ? styles.viewBtn : 
                                                                             task.status === 'inprogress' ? styles.submitBtn : 
                                                                             styles.startBtn)
                                                                    }}
                                                                >
                                                                    <i className={getActionButtonIcon(task.status) + ' me-1'}></i>
                                                                    {getActionButtonText(task.status)}
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Task Submission Modal */}
                {showSubmissionForm && selectedTask && (
                    <div className="modal-overlay" style={styles.modalOverlay}>
                        <div className="modal-content" style={styles.modalContent}>
                            <div className="modal-header" style={styles.modalHeader}>
                                <h3 style={{color: colors.textPrimary, margin: 0}}>
                                    <i className="fas fa-tasks me-2"></i>
                                    Update Task Status
                                </h3>
                                <button 
                                    className="modal-close"
                                    onClick={() => setShowSubmissionForm(false)}
                                    style={styles.modalClose}
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            <div className="modal-body" style={styles.modalBody}>
                                {/* Task Info Preview */}
                                <div className="task-info-preview" style={styles.taskInfoPreview}>
                                    <div className="card-header" style={styles.cardHeader}>
                                        <h4 style={{color: colors.textPrimary, margin: 0}}>Task Details</h4>
                                    </div>
                                    <div className="card-body" style={styles.cardBody}>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <p style={styles.taskDetail}><strong>Task Name:</strong> {selectedTask.video_name}</p>
                                                <p style={styles.taskDetail}><strong>Description:</strong> {selectedTask.description}</p>
                                            </div>
                                            <div className="col-md-6">
                                                <p style={styles.taskDetail}><strong>Raw Link:</strong> {selectedTask.raw_link ? (
                                                    <a href={selectedTask.raw_link} target="_blank" rel="noopener noreferrer" style={styles.taskLink}>
                                                        {selectedTask.raw_link}
                                                    </a>
                                                ) : 'No link provided'}</p>
                                                <p style={styles.taskDetail}><strong>Current Status:</strong> {getStatusBadge(selectedTask.status)}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Submission Form */}
                                <form onSubmit={handleSubmitTask} className="submission-form" style={styles.submissionForm}>
                                    <div className="row">
                                        <div className="col-12 mb-3">
                                            <label className="form-label" style={styles.formLabel}>Update Status *</label>
                                            <select
                                                className="form-select"
                                                style={styles.formSelect}
                                                name="status"
                                                value={submissionData.status}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="todo">To Do</option>
                                                <option value="inprogress">In Progress</option>
                                                <option value="completed">Completed</option>
                                            </select>
                                            <div className="form-text" style={styles.formText}>
                                                {submissionData.status === 'todo' && (
                                                    <span style={{color: colors.warning}}>
                                                        <i className="fas fa-info-circle me-1"></i>
                                                        Mark as To Do if you haven't started working yet
                                                    </span>
                                                )}
                                                {submissionData.status === 'inprogress' && (
                                                    <span style={{color: colors.accent}}>
                                                        <i className="fas fa-info-circle me-1"></i>
                                                        Mark as In Progress if you're currently working on this task
                                                    </span>
                                                )}
                                                {submissionData.status === 'completed' && (
                                                    <span style={{color: colors.success}}>
                                                        <i className="fas fa-info-circle me-1"></i>
                                                        Mark as Completed when you've finished the task
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label" style={styles.formLabel}>
                                                <span style={{color: colors.accent, fontSize: '0.85rem'}}> Video URL( Optional)</span>
                                            </label>
                                            <input
                                                type="url"
                                                className="form-control"
                                                style={styles.formControl}
                                                name="url"
                                                value={submissionData.url}
                                                onChange={handleInputChange}
                                                placeholder="https://example.com/design-work (optional)"
                                            />
                                            <div className="form-text" style={styles.formText}>
                                                <span style={{color: colors.textTertiary}}>
                                                    <i className="fas fa-info-circle me-1"></i>
                                                    Optional: Provide the link to your completed design work
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label" style={styles.formLabel}>
                                                 <span style={{color: colors.accent, fontSize: '0.85rem'}}>Thumbnail URL(Optional)</span>
                                            </label>
                                            <input
                                                type="url"
                                                className="form-control"
                                                style={styles.formControl}
                                                name="thumbnail_url"
                                                value={submissionData.thumbnail_url}
                                                onChange={handleInputChange}
                                                placeholder="https://example.com/thumbnail.jpg (optional)"
                                            />
                                            <div className="form-text" style={styles.formText}>
                                                <span style={{color: colors.textTertiary}}>
                                                    <i className="fas fa-info-circle me-1"></i>
                                                    Optional: Provide a thumbnail preview image URL
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div className="col-12 mb-3">
                                            <label className="form-label" style={styles.formLabel}>Notes</label>
                                            <textarea
                                                className="form-control"
                                                style={styles.formControl}
                                                name="notes"
                                                value={submissionData.notes}
                                                onChange={handleInputChange}
                                                placeholder="Any additional notes or comments about this work..."
                                                rows="4"
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="form-actions" style={styles.formActions}>
                                        <button
                                            type="button"
                                            className="btn-cancel"
                                            onClick={() => setShowSubmissionForm(false)}
                                            style={styles.cancelBtn}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn-submit"
                                            disabled={submitting}
                                            style={{
                                                ...styles.submitBtn,
                                                background: submissionData.status === 'todo' ? colors.secondary :
                                                           submissionData.status === 'inprogress' ? colors.accent :
                                                           colors.success
                                            }}
                                        >
                                            {submitting ? (
                                                <>
                                                    <i className="fas fa-spinner fa-spin me-2"></i>
                                                    Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <i className={getSubmitButtonIcon() + ' me-2'}></i>
                                                    {getSubmitButtonText()}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* Task Details Modal */}
                {showTaskDetailsModal && selectedTask && (
                    <div className="modal-overlay" style={styles.modalOverlay}>
                        <div className="modal-content" style={styles.modalContent}>
                            <div className="modal-header" style={styles.modalHeader}>
                                <h3 style={{color: colors.textPrimary, margin: 0}}>
                                    <i className="fas fa-info-circle me-2"></i>
                                    Task Details
                                </h3>
                                <button 
                                    className="modal-close"
                                    onClick={() => setShowTaskDetailsModal(false)}
                                    style={styles.modalClose}
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            <div className="modal-body" style={styles.modalBody}>
                                <div className="task-details-grid">
                                    {/* Basic Task Information */}
                                    <div className="details-section" style={styles.detailsSection}>
                                        <div className="card-header" style={styles.cardHeader}>
                                            <h4 style={{color: colors.textPrimary, margin: 0}}>Task Information</h4>
                                        </div>
                                        <div className="card-body" style={styles.cardBody}>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <p style={styles.taskDetail}><strong>Task Name:</strong> {selectedTask.video_name || 'Not specified'}</p>
                                                    <p style={styles.taskDetail}><strong>Description:</strong> {selectedTask.description || 'No description provided'}</p>
                                                    <p style={styles.taskDetail}><strong>Raw Link:</strong> {selectedTask.raw_link ? (
                                                        <a href={selectedTask.raw_link} target="_blank" rel="noopener noreferrer" style={styles.taskLink}>
                                                            {selectedTask.raw_link}
                                                        </a>
                                                    ) : 'No link provided'}</p>
                                                </div>
                                                <div className="col-md-6">
                                                    <p style={styles.taskDetail}><strong>Status:</strong> {getStatusBadge(selectedTask.status)}</p>
                                                    <p style={styles.taskDetail}><strong>Assigned Date:</strong> {formatDateTime(selectedTask.created_at)}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Designer Submission Details */}
                                    <div className="details-section" style={styles.detailsSection}>
                                        <div className="card-header" style={styles.cardHeader}>
                                            <h4 style={{color: colors.textPrimary, margin: 0}}>Designer Submission</h4>
                                        </div>
                                        <div className="card-body" style={styles.cardBody}>
                                            {selectedTask.thumbnail_url || selectedTask.submitted_url || selectedTask.designer_notes ? (
                                                <div className="row">
                                                    {selectedTask.thumbnail_url && (
                                                        <div className="col-12 mb-3">
                                                            <p style={styles.taskDetail}><strong>Thumbnail:</strong></p>
                                                            <div className="thumbnail-preview" style={styles.thumbnailPreview}>
                                                                <img 
                                                                    src={selectedTask.thumbnail_url} 
                                                                    alt="Design thumbnail" 
                                                                    style={styles.thumbnailImage}
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                    }}
                                                                />
                                                                <a href={selectedTask.thumbnail_url} target="_blank" rel="noopener noreferrer" style={styles.viewLink}>
                                                                    View Thumbnail
                                                                </a>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {selectedTask.submitted_url && (
                                                        <div className="col-12 mb-3">
                                                            <p style={styles.taskDetail}><strong>Submitted URL:</strong></p>
                                                            <a href={selectedTask.submitted_url} target="_blank" rel="noopener noreferrer" style={styles.taskLink}>
                                                                {selectedTask.submitted_url}
                                                            </a>
                                                        </div>
                                                    )}
                                                    {selectedTask.designer_notes && (
                                                        <div className="col-12 mb-3">
                                                            <p style={styles.taskDetail}><strong>Designer Notes:</strong></p>
                                                            <div style={styles.notesContent}>
                                                                {selectedTask.designer_notes}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {selectedTask.submitted_at && (
                                                        <div className="col-12">
                                                            <p style={styles.taskDetail}><strong>Submitted At:</strong> {formatDateTime(selectedTask.submitted_at)}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="empty-submission" style={styles.emptySubmission}>
                                                    <i className="fas fa-inbox" style={styles.emptySubmissionIcon}></i>
                                                    <p style={styles.emptySubmissionText}>No submission details available</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-actions" style={styles.modalActions}>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setShowTaskDetailsModal(false)}
                                        style={styles.closeBtn}
                                    >
                                        <i className="fas fa-times me-2"></i>
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <style jsx>{`
                    .spinning {
                        animation: spin 1s linear infinite;
                    }

                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }

                    @keyframes float {
                        0%, 100% { transform: translate(0px, 0px) scale(1); }
                        33% { transform: translate(30px, -50px) scale(1.1); }
                        66% { transform: translate(-20px, 20px) scale(0.9); }
                    }

                    @keyframes wave {
                        0% { transform: translateX(-100%); }
                        100% { transform: translateX(100%); }
                    }

                    @keyframes bounce {
                        0%, 80%, 100% {
                            transform: scale(0);
                        } 40% {
                            transform: scale(1);
                        }
                    }
                    
                    /* Custom Hover Effects */
                    .stat-card:hover .stat-glow {
                        opacity: 0.15;
                    }
                    .action-btn:hover {
                        transform: translateY(-2px);
                    }
                    .filter-btn:hover {
                        transform: translateY(-1px);
                    }
                    .table-row:hover {
                        background: rgba(255, 255, 255, 0.05);
                    }
                `}</style>
            </div>
        </Layout>
    );
}

const styles = {
    container: {
        minHeight: '100vh',
        background: `linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)`,
        position: 'relative',
        overflowX: 'hidden'
    },
    animatedBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none'
    },
    floatingOrb: {
        position: 'absolute',
        borderRadius: '50%',
        filter: 'blur(40px)',
        opacity: 0.3,
        animation: 'float 8s ease-in-out infinite'
    },
    orb1: {
        width: '300px',
        height: '300px',
        background: colors.primary,
        top: '10%',
        left: '5%',
        animationDelay: '0s'
    },
    orb2: {
        width: '200px',
        height: '200px',
        background: colors.secondary,
        top: '60%',
        right: '10%',
        animationDelay: '2s'
    },
    orb3: {
        width: '250px',
        height: '250px',
        background: colors.accent,
        bottom: '20%',
        left: '15%',
        animationDelay: '4s'
    },
    orb4: {
        width: '180px',
        height: '180px',
        background: colors.success,
        top: '30%',
        right: '20%',
        animationDelay: '6s'
    },
    gridOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
    },
    header: {
        padding: '40px 40px 30px',
        position: 'relative',
        zIndex: 2
    },
    headerContent: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        maxWidth: '1400px',
        margin: '0 auto'
    },
    greeting: {
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        marginBottom: '15px'
    },
    pageTitle: {
        fontSize: '3rem',
        fontWeight: '800',
        background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        margin: 0
    },
    welcomeBadge: {
        background: colors.backgroundMedium,
        padding: '8px 16px',
        borderRadius: '20px',
        border: `1px solid ${colors.borderDark}`,
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    badgeText: {
        color: colors.textPrimary,
        fontWeight: '600',
        fontSize: '0.9rem'
    },
    badgeRole: {
        background: colors.primary,
        color: colors.textPrimary,
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.7rem',
        fontWeight: '600',
        textTransform: 'uppercase'
    },
    pageSubtitle: {
        color: colors.textSecondary,
        fontSize: '1.2rem',
        margin: 0,
        maxWidth: '600px'
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '30px'
    },
    timeDisplay: {
        textAlign: 'right',
        color: colors.textPrimary
    },
    currentTime: {
        fontSize: '2rem',
        fontWeight: '700',
        marginBottom: '4px'
    },
    currentDate: {
        fontSize: '0.9rem',
        opacity: 0.8
    },
    refreshBtn: {
        background: colors.backgroundMedium,
        color: colors.textPrimary,
        border: `1px solid ${colors.borderDark}`,
        padding: '12px 20px',
        borderRadius: '15px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    refreshIcon: {
        fontSize: '1.1rem'
    },
    messageAlert: {
        color: colors.textPrimary,
        padding: '20px',
        borderRadius: '15px',
        margin: '0 40px 30px',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        maxWidth: '1400px',
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    alertIcon: {
        fontSize: '1.5rem'
    },
    alertMessage: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    alertClose: {
        background: 'none',
        border: 'none',
        color: 'inherit',
        cursor: 'pointer',
        padding: '8px',
        borderRadius: '8px',
        transition: 'background 0.3s ease'
    },
    content: {
        padding: '0 40px 40px',
        maxWidth: '1400px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 2
    },
    statsSection: {
        marginBottom: '40px'
    },
    sectionTitle: {
        marginBottom: '30px'
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '25px'
    },
    statCard: {
        background: colors.backgroundMedium,
        borderRadius: '25px',
        padding: '30px',
        position: 'relative',
        overflow: 'hidden',
        border: `1px solid ${colors.borderDark}`,
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        cursor: 'pointer'
    },
    statGlow: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'inherit',
        filter: 'blur(20px)',
        opacity: 0,
        transition: 'opacity 0.3s ease'
    },
    statContent: {
        position: 'relative',
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        gap: '20px'
    },
    statIcon: {
        width: '80px',
        height: '80px',
        borderRadius: '20px',
        background: 'rgba(255, 255, 255, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '2rem',
        color: colors.textPrimary
    },
    statInfo: {
        flex: 1
    },
    statNumber: {
        fontSize: '2.5rem',
        fontWeight: '800',
        color: colors.textPrimary,
        margin: '0 0 5px 0',
        lineHeight: 1
    },
    statLabel: {
        fontSize: '1.1rem',
        fontWeight: '600',
        color: colors.textPrimary,
        margin: '0 0 5px 0'
    },
    statTrend: {
        color: colors.textSecondary,
        fontSize: '0.9rem'
    },
    statWave: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        height: '4px',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
        animation: 'wave 2s linear infinite'
    },
    filtersSection: {
        marginBottom: '40px'
    },
    searchCard: {
        background: colors.backgroundMedium,
        borderRadius: '20px',
        border: `1px solid ${colors.borderDark}`,
        backdropFilter: 'blur(10px)'
    },
    cardBody: {
        padding: '25px'
    },
    inputGroupText: {
        background: colors.backgroundMedium,
        border: `1px solid ${colors.borderDark}`,
        borderRight: 'none',
        color: colors.textSecondary
    },
    searchInput: {
        background: colors.backgroundMedium,
        border: `1px solid ${colors.borderDark}`,
        borderLeft: 'none',
        color: colors.textPrimary,
        backdropFilter: 'blur(10px)',
        '&::placeholder': {
            color: colors.textTertiary
        }
    },
    filterBtn: {
        background: colors.backgroundMedium,
        color: colors.textSecondary,
        border: `1px solid ${colors.borderDark}`,
        padding: '8px 16px',
        borderRadius: '20px',
        cursor: 'pointer',
        fontSize: '0.85rem',
        fontWeight: '600',
        transition: 'all 0.3s ease',
        backdropFilter: 'blur(10px)'
    },
    filterBtnActive: {
        background: colors.primary,
        color: colors.textPrimary,
        borderColor: colors.primary
    },
    tasksSection: {
        marginBottom: '40px'
    },
    sectionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '25px'
    },
    tasksCount: {
        background: colors.primary,
        color: colors.textPrimary,
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '0.85rem',
        fontWeight: '600'
    },
    tasksCard: {
        background: colors.backgroundMedium,
        borderRadius: '20px',
        border: `1px solid ${colors.borderDark}`,
        backdropFilter: 'blur(10px)',
        overflow: 'hidden'
    },
    emptyState: {
        textAlign: 'center',
        padding: '60px 40px',
        color: colors.textSecondary
    },
    emptyIcon: {
        fontSize: '4rem',
        marginBottom: '20px',
        opacity: 0.5,
        color: colors.textTertiary
    },
    emptyTitle: {
        color: colors.textPrimary,
        marginBottom: '10px'
    },
    emptyText: {
        margin: '0 0 20px 0',
        color: colors.textSecondary
    },
    clearSearchBtn: {
        background: colors.primary,
        color: colors.textPrimary,
        border: 'none',
        padding: '8px 20px',
        borderRadius: '20px',
        cursor: 'pointer',
        fontWeight: '600'
    },
    tableContainer: {
        overflowX: 'auto'
    },
    tasksTable: {
        width: '100%',
        borderCollapse: 'collapse'
    },
    tableHeader: {
        background: colors.backgroundMedium,
        color: colors.textPrimary,
        padding: '15px 12px',
        textAlign: 'left',
        fontWeight: '600',
        fontSize: '0.85rem',
        borderBottom: `2px solid ${colors.borderDark}`,
        whiteSpace: 'nowrap'
    },
    tableRow: {
        borderBottom: `1px solid ${colors.borderSecondary}`,
        transition: 'background 0.3s ease'
    },
    tableCell: {
        padding: '12px',
        borderBottom: `1px solid ${colors.borderSecondary}`
    },
    linkBtn: {
        background: colors.primary,
        color: colors.textPrimary,
        border: 'none',
        padding: '6px 12px',
        borderRadius: '15px',
        fontSize: '0.8rem',
        fontWeight: '600',
        cursor: 'pointer',
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center'
    },
    noLink: {
        color: colors.textTertiary,
        fontSize: '0.85rem'
    },
    actionBtn: {
        border: 'none',
        padding: '8px 16px',
        borderRadius: '15px',
        fontSize: '0.8rem',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        display: 'inline-flex',
        alignItems: 'center'
    },
    startBtn: {
        background: colors.primary,
        color: colors.textPrimary
    },
    submitBtn: {
        background: colors.warning,
        color: colors.textPrimary
    },
    viewBtn: {
        background: colors.accent,
        color: colors.textPrimary
    },
    // Modal Styles
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: '20px'
    },
    modalContent: {
        background: colors.backgroundMedium,
        borderRadius: '20px',
        border: `1px solid ${colors.borderDark}`,
        width: '90%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'auto',
        backdropFilter: 'blur(10px)'
    },
    modalHeader: {
        padding: '25px 30px',
        borderBottom: `1px solid ${colors.borderDark}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    modalClose: {
        background: 'none',
        border: 'none',
        color: colors.textSecondary,
        fontSize: '1.2rem',
        cursor: 'pointer',
        padding: '8px',
        borderRadius: '8px',
        transition: 'background 0.3s ease'
    },
    modalBody: {
        padding: '30px'
    },
    taskInfoPreview: {
        background: colors.backgroundMedium,
        borderRadius: '15px',
        border: `1px solid ${colors.borderDark}`,
        marginBottom: '25px',
        overflow: 'hidden'
    },
    cardHeader: {
        background: colors.backgroundMedium,
        padding: '15px 20px',
        borderBottom: `1px solid ${colors.borderDark}`
    },
    taskDetail: {
        color: colors.textSecondary,
        margin: '0 0 10px 0'
    },
    taskLink: {
        color: colors.primary,
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline'
        }
    },
    submissionForm: {
        marginTop: '20px'
    },
    formLabel: {
        color: colors.textPrimary,
        fontWeight: '600',
        marginBottom: '8px',
        display: 'block'
    },
    formSelect: {
        background: colors.backgroundMedium,
        border: `1px solid ${colors.borderDark}`,
        color: colors.textPrimary,
        backdropFilter: 'blur(10px)',
        // Custom dropdown arrow
        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='white' viewBox='0 0 20 20'%3e%3cpath d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 0.75rem center',
        backgroundSize: '16px 12px',
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        '&:focus': {
            borderColor: colors.primary,
            boxShadow: `0 0 0 0.2rem rgba(99, 102, 241, 0.25)`
        }
    },
    formControl: {
        background: colors.backgroundMedium,
        border: `1px solid ${colors.borderDark}`,
        color: colors.textPrimary,
        backdropFilter: 'blur(10px)',
        '&:focus': {
            borderColor: colors.primary,
            boxShadow: `0 0 0 0.2rem rgba(99, 102, 241, 0.25)`,
            color: colors.textPrimary
        },
        '&::placeholder': {
            color: colors.textTertiary
        }
    },
    formText: {
        marginTop: '8px',
        fontSize: '0.85rem'
    },
    formActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '15px',
        marginTop: '25px'
    },
    cancelBtn: {
        background: colors.backgroundMedium,
        color: colors.textPrimary,
        border: `1px solid ${colors.borderDark}`,
        padding: '10px 20px',
        borderRadius: '15px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)'
    },
    submitBtn: {
        color: colors.textPrimary,
        border: 'none',
        padding: '10px 25px',
        borderRadius: '15px',
        cursor: 'pointer',
        fontWeight: '600',
        transition: 'all 0.3s ease'
    },
    detailsSection: {
        background: colors.backgroundMedium,
        borderRadius: '15px',
        border: `1px solid ${colors.borderDark}`,
        marginBottom: '25px',
        overflow: 'hidden'
    },
    thumbnailPreview: {
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        marginTop: '10px'
    },
    thumbnailImage: {
        width: '100px',
        height: '100px',
        borderRadius: '10px',
        objectFit: 'cover',
        border: `1px solid ${colors.borderDark}`
    },
    viewLink: {
        color: colors.primary,
        textDecoration: 'none',
        fontWeight: '600',
        '&:hover': {
            textDecoration: 'underline'
        }
    },
    notesContent: {
        background: colors.backgroundMedium,
        padding: '15px',
        borderRadius: '10px',
        color: colors.textSecondary,
        whiteSpace: 'pre-wrap',
        marginTop: '10px',
        border: `1px solid ${colors.borderSecondary}`
    },
    emptySubmission: {
        textAlign: 'center',
        padding: '40px 20px',
        color: colors.textSecondary
    },
    emptySubmissionIcon: {
        fontSize: '3rem',
        marginBottom: '15px',
        opacity: 0.5,
        color: colors.textTertiary
    },
    emptySubmissionText: {
        margin: 0,
        color: colors.textSecondary
    },
    modalActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '25px'
    },
    closeBtn: {
        background: colors.backgroundMedium,
        color: colors.textPrimary,
        border: `1px solid ${colors.borderDark}`,
        padding: '10px 20px',
        borderRadius: '15px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)'
    },
    loadingDots: {
        display: 'flex',
        gap: '8px',
        justifyContent: 'center',
        marginBottom: '20px'
    },
    dot: {
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        background: colors.primary,
        animation: 'bounce 1.4s ease-in-out infinite both'
    },
    loadingText: {
        fontSize: '1.8rem',
        margin: '0 0 12px 0',
        fontWeight: '600',
        color: colors.textPrimary
    },
    loadingSubtext: {
        color: colors.textSecondary,
        margin: 0,
        fontSize: '1.1rem'
    }
};

// Global styles for dropdown options
const globalStyles = `
    /* Style for dropdown options */
    select option {
        background-color: #302b63 !important;
        color: rgba(255, 255, 255, 0.8) !important;
        padding: 10px !important;
    }
    
    select option:hover,
    select option:focus,
    select option:checked {
        background-color: #6366F1 !important;
        color: white !important;
    }
    
    /* Remove default browser styling */
    select {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
    }
    
    /* Custom scrollbar for dropdown */
    select::-webkit-scrollbar {
        width: 8px;
    }
    
    select::-webkit-scrollbar-track {
        background: #302b63;
        border-radius: 4px;
    }
    
    select::-webkit-scrollbar-thumb {
        background: #6366F1;
        border-radius: 4px;
    }
    
    select::-webkit-scrollbar-thumb:hover {
        background: #8B5CF6;
    }
`;

// Add global styles to document
if (typeof document !== 'undefined') {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = globalStyles;
    document.head.appendChild(styleElement);
}

export default MyWork;